import { useQuery } from '@tanstack/react-query';
import { Banknote, Check, Copy, MapPin, Ruler, Truck, X } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { resolveCheckoutOptions, resolveShipping } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { PIN_CODE, describeDeliveryEstimate } from '@/features/storefront/delivery';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { useDeliveryPincode } from '@/features/storefront/hooks/useDeliveryPincode';
import { usePartsEnvironment, useProductParts } from '@/features/storefront/parts/context';
import type { ProductPartProps } from '@/features/storefront/parts/ProductParts';
import { partOwnsSpacing } from '@/features/storefront/parts/style';
import {
  parseOfferLines,
  parseSizeChart,
  partChoice,
  partFlag,
  partNumber,
  type PagePart,
} from '@/features/storefront/sections/parts';
import { STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';
import { formatMoney } from '@/lib/money';

/**
 * The product page's selling aids: when it would arrive, how few are left,
 * which codes take money off, a buy bar that follows the shopper down the page
 * and the size chart.
 *
 * None of them is on any page until a merchant adds it - see `partDocument.ts`
 * - and each draws nothing when it has nothing true to say: no stock count is
 * invented, no delivery date guessed past what the shop's own settings say.
 *
 * In the customiser (`includeHidden`) none of them asks the server anything,
 * because the dashboard is not the shop's host; each shows what it would look
 * like instead, so a merchant who adds one sees it.
 */

/** The margin a part had before the merchant chose one. See `parts/style.ts`. */
function ownClass(part: PagePart, fallback: string): string | undefined {
  return partOwnsSpacing(part) ? fallback : undefined;
}

function useInEditor(): boolean {
  return usePartsEnvironment()?.includeHidden ?? false;
}

// --- Delivery date check ------------------------------------------------------

/**
 * A PIN code box that answers "when, and can I pay cash".
 *
 * Asks the shop's own delivery quote for one of the chosen variant - the same
 * calculation the till runs, so the product page cannot promise a date the
 * checkout then contradicts. Where the shop has not switched delivery pricing
 * on there is nothing to ask, and the answer is the merchant's own range.
 */
export function ProductDeliveryEstimatePart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const features = useStorefrontFeatures();
  const editor = useInEditor();
  const inputId = useId();

  // The remembered PIN code is shared with the header's "Deliver to" box
  // (`useDeliveryPincode`): one typed there shows here, and the other way.
  const [saved, save] = useDeliveryPincode(source.key, !editor);
  const [pincode, setPincode] = useState(saved);
  const [draft, setDraft] = useState(pincode);
  const [invalid, setInvalid] = useState(false);
  const [followed, setFollowed] = useState(saved);
  if (saved !== followed) {
    setFollowed(saved);
    if (saved) {
      setPincode(saved);
      setDraft(saved);
      setInvalid(false);
    }
  }

  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled: !editor,
  });
  const shipping = resolveShipping(context.data);
  const variantId = product?.selected?.id ?? '';
  const asks = !editor && features.cart && shipping.enabled && Boolean(pincode) && Boolean(variantId);
  const quote = useQuery({
    queryKey: [...scope, 'delivery-estimate', variantId, pincode],
    queryFn: () => source.shippingOptions({ variantId, postalCode: pincode }),
    enabled: asks,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!product) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = draft.trim();
    if (!PIN_CODE.test(next)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setPincode(next);
    save(next);
  };

  const range = { min: partNumber(part, 'min_days') || 3, max: partNumber(part, 'max_days') || 7 };
  const codOffered = editor || resolveCheckoutOptions(context.data).some((option) => option.method === 'COD');
  const heading = part.label.trim() || text('product_page.delivery_check');

  let answer: ReactNode = null;
  if (pincode) {
    if (asks && quote.isPending) {
      answer = <p style={{ color: 'var(--storefront-muted)' }}>{text('checkout.delivery_loading')}</p>;
    } else if (asks && quote.isError) {
      answer = <p role="alert">{text('checkout.delivery_error')}</p>;
    } else if (asks && quote.data && !quote.data.serviceable) {
      answer = <p role="alert">{quote.data.message || text('checkout.delivery_unserviceable')}</p>;
    } else {
      const data = asks ? quote.data : undefined;
      const option = data?.options[0];
      const cod = codOffered && (data ? data.cod_available && (option?.cod_available ?? true) : true);
      answer = (
        <ul className="space-y-1.5">
          <li className="flex items-start gap-2">
            <Truck className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="font-medium">{describeDeliveryEstimate(data, range, text)}</span>
          </li>
          {partFlag(part, 'show_cod') ? (
            <li className="flex items-start gap-2" data-cod={cod ? 'yes' : 'no'}>
              <Banknote className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span style={cod ? undefined : { color: 'var(--storefront-muted)' }}>
                {cod ? text('product_page.cod_available') : text('checkout.delivery_cod_unavailable')}
              </span>
            </li>
          ) : null}
        </ul>
      );
    }
  }

  return (
    <div
      className={cn(ownClass(part, 'mt-6'), 'border p-4 text-sm')}
      style={{ borderColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
    >
      <label htmlFor={inputId} className="flex items-center gap-2 font-semibold">
        <MapPin className="size-4 shrink-0" aria-hidden />
        {heading}
      </label>
      {pincode && !invalid ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <span>{text('product_page.delivering_to', { pincode })}</span>
          <button
            type="button"
            className="text-sm font-semibold underline underline-offset-2"
            style={{ color: 'var(--storefront-primary)' }}
            onClick={() => {
              setDraft(pincode);
              setPincode('');
            }}
          >
            {text('product_page.pincode_change')}
          </button>
        </div>
      ) : (
        <form className="mt-3 flex gap-2" onSubmit={submit} noValidate>
          <input
            id={inputId}
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={6}
            value={draft}
            placeholder={text('product_page.pincode_placeholder')}
            aria-invalid={invalid || undefined}
            onChange={(event) => setDraft(event.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-10 min-w-0 flex-1 border bg-transparent px-3 text-sm"
            style={{ borderColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
          />
          <button
            type="submit"
            className="h-10 shrink-0 px-4 text-sm font-semibold text-white"
            style={{ backgroundColor: 'var(--storefront-primary)', borderRadius: 'var(--storefront-radius)' }}
          >
            {text('product_page.pincode_check')}
          </button>
        </form>
      )}
      {invalid ? (
        <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--storefront-primary)' }}>
          {text('product_page.pincode_invalid')}
        </p>
      ) : null}
      {answer && !invalid ? <div className="mt-3">{answer}</div> : null}
    </div>
  );
}

// --- Low stock ------------------------------------------------------------------

/**
 * "Only 4 left", while that is true.
 *
 * The count is the server's, sent only while this component is on the shop's
 * live theme and only for tracked stock at or under fifty - so an untracked
 * product, a sold-out one or a well-stocked one draws nothing at all, and the
 * merchant's threshold narrows that further.
 */
export function ProductStockBarPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  const editor = useInEditor();
  if (!product) return null;

  const threshold = partNumber(part, 'threshold') || 10;
  const real = product.selected?.in_stock ? product.selected.stock_left : null;
  // The customiser cannot know a count, so it shows the bar as it would look.
  const left = typeof real === 'number' ? real : editor ? Math.min(3, threshold) : null;
  if (left === null || left <= 0 || left > threshold) return null;

  const words = text('product_page.stock_left', { count: left });
  if (partChoice(part, 'style') === 'text') {
    return <p className={cn(ownClass(part, 'mt-3'), 'font-semibold')}>{words}</p>;
  }

  const filled = Math.max(6, Math.round((left / threshold) * 100));
  return (
    <div className={ownClass(part, 'mt-3')}>
      <p className="font-semibold">{words}</p>
      <div
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: 'var(--storefront-surface)' }}
        aria-hidden
      >
        <div
          data-testid="stock-bar-fill"
          className="h-full rounded-full"
          style={{ width: `${filled}%`, backgroundColor: 'var(--storefront-primary)' }}
        />
      </div>
    </div>
  );
}

// --- Offers ---------------------------------------------------------------------

function CopyCode({ code }: { code: string }) {
  const text = useShopContent();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        // A browser without clipboard access still shows the code to type.
        void navigator.clipboard?.writeText(code).then(
          () => setCopied(true),
          () => undefined,
        );
      }}
      className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold"
      style={{ color: 'var(--storefront-primary)' }}
      aria-label={`${text('product_page.copy_code')} ${code}`}
    >
      {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      <span aria-live="polite">{copied ? text('product_page.code_copied') : text('product_page.copy_code')}</span>
    </button>
  );
}

/**
 * The merchant's codes, each beside what it gives.
 *
 * Merchant-written rather than read from the shop's discounts: a discount has
 * no "show it to everyone" switch, and a code meant for one customer or one
 * campaign must never appear on every product page because it exists.
 */
export function ProductOffersPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const offers = parseOfferLines(part.body);
  if (offers.length === 0) return null;

  const heading = part.label.trim() || text('product_page.offers');
  const copyable = partFlag(part, 'show_copy');

  return (
    <div className={ownClass(part, 'mt-6')}>
      <p className="text-sm font-semibold" style={STOREFRONT_HEADING_STYLE}>
        {heading}
      </p>
      <ul className="mt-2 space-y-2">
        {offers.map((offer, at) => (
          <li
            key={`${offer.code}-${at}`}
            className="flex items-center gap-3 border border-dashed px-3 py-2.5 text-sm"
            style={{ borderColor: 'var(--storefront-primary)', borderRadius: 'var(--storefront-radius)' }}
          >
            <span className="min-w-0 flex-1">
              {offer.code ? (
                <span
                  className="me-2 inline-block px-1.5 py-0.5 font-mono text-xs font-bold tracking-wide"
                  style={{ backgroundColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
                >
                  {offer.code}
                </span>
              ) : null}
              {offer.description}
            </span>
            {offer.code && copyable ? <CopyCode code={offer.code} /> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Sticky add-to-cart -------------------------------------------------------

/**
 * Whether the page's own add-to-cart button has scrolled up out of view.
 *
 * Watches the button's part (`part-<id>`, which `PartRegion` gives every part)
 * rather than the button itself, so a merchant who restyles the button changes
 * nothing here. A page with no such button has nothing to stand in for and
 * shows the bar from the start.
 */
function useMainButtonGone(targetId: string | null, enabled: boolean): boolean {
  const [gone, setGone] = useState(targetId === null);

  useEffect(() => {
    if (!enabled) return;
    if (targetId === null) {
      setGone(true);
      return;
    }
    const element = document.getElementById(targetId);
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      // Only once it is *above* the screen: a button still below the fold is
      // one the shopper has not reached, and a bar duplicating it is noise.
      setGone(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [targetId, enabled]);

  return gone;
}

export function ProductStickyCartPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const product = useProductParts();
  const environment = usePartsEnvironment();
  const features = useStorefrontFeatures();
  const editor = environment?.includeHidden ?? false;
  const main = environment?.parts.find((each) => each.type === 'product_add_to_cart' && each.visible);
  const gone = useMainButtonGone(main ? `part-${main.id}` : null, !editor);
  if (!product || !features.cart) return null;

  const { detail, selected } = product;
  const inStock = selected?.in_stock ?? false;
  const image = detail.images[0];
  const phonesOnly = partChoice(part, 'show_on') !== 'all';

  const bar = (
    <div className="mx-auto flex w-full max-w-7xl items-center gap-3">
      {image && partFlag(part, 'show_image') ? (
        <StorefrontImage
          url={image.url}
          sizes={fixedImageSizes(48)}
          alt=""
          className="size-12 shrink-0 object-cover"
          style={{ borderRadius: 'var(--storefront-radius)' }}
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{detail.name}</p>
        <p className="text-sm">{formatMoney(selected?.price ?? detail.price)}</p>
      </div>
      <button
        type="button"
        disabled={!inStock || product.adding}
        onClick={() => product.onAddToCart(product.quantity)}
        className="inline-flex h-11 shrink-0 items-center justify-center px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: 'var(--storefront-primary)', borderRadius: 'var(--storefront-radius)' }}
      >
        {!inStock
          ? text('product.out_of_stock')
          : product.adding
            ? text('product.adding')
            : part.label.trim() || text('product.add_to_cart')}
      </button>
    </div>
  );

  // In the customiser it stands where it was put, so it can be seen and
  // selected without covering the editor's own controls.
  if (editor) {
    return (
      <div
        className="border-t px-4 py-3"
        style={{ borderColor: 'var(--storefront-surface)', backgroundColor: 'var(--storefront-background)' }}
      >
        {bar}
      </div>
    );
  }

  if (!gone) return null;
  return (
    <div
      data-testid="sticky-cart"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t px-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]',
        phonesOnly && 'sm:hidden',
      )}
      style={{
        borderColor: 'var(--storefront-surface)',
        backgroundColor: 'var(--storefront-background)',
        color: 'var(--storefront-text)',
        // Standing on the app bar where the shop has one, which already
        // clears the home indicator (`index.css`).
        bottom: 'var(--storefront-app-bar, 0px)',
        paddingBottom: 'calc(0.75rem + var(--storefront-sticky-inset, env(safe-area-inset-bottom, 0px)))',
      }}
    >
      {bar}
    </div>
  );
}

// --- Size chart -----------------------------------------------------------------

function SizeChartDialog({ part, title, onClose }: { part: PagePart; title: string; onClose: () => void }) {
  const text = useShopContent();
  const titleId = useId();
  const table = parseSizeChart(part.body);
  const image = partChoice(part, 'image_url').trim();
  const note = partChoice(part, 'note').trim();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-6" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[85vh] w-full overflow-y-auto p-5 sm:max-w-2xl"
        style={{
          backgroundColor: 'var(--storefront-background)',
          color: 'var(--storefront-text)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-lg font-semibold" style={STOREFRONT_HEADING_STYLE}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 place-items-center rounded-full"
            aria-label={text('common.close')}
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
        {image ? <StorefrontImage url={image} alt={title} className="mt-4 w-full object-contain" /> : null}
        {table ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {table.header.map((cell, at) => (
                    <th
                      key={at}
                      scope="col"
                      className="border-b px-3 py-2 text-start font-semibold"
                      style={{ borderColor: 'var(--storefront-surface)', backgroundColor: 'var(--storefront-surface)' }}
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, at) => (
                  <tr key={at}>
                    {row.map((cell, column) => (
                      <td
                        key={column}
                        className="border-b px-3 py-2"
                        style={{ borderColor: 'var(--storefront-surface)' }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {note ? (
          <p className="mt-3 text-xs" style={{ color: 'var(--storefront-muted)' }}>
            {note}
          </p>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

/** A "Size chart" link, and the merchant's table - or picture of one - it opens. */
export function ProductSizeChartPart({ part }: ProductPartProps) {
  const text = useShopContent();
  const [open, setOpen] = useState(false);
  const hasTable = parseSizeChart(part.body) !== null;
  const hasImage = partChoice(part, 'image_url').trim() !== '';
  if (!hasTable && !hasImage) return null;

  const title = part.label.trim() || text('product_page.size_chart');
  return (
    <div className={ownClass(part, 'mt-4')}>
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4"
      >
        <Ruler className="size-4" aria-hidden />
        {title}
      </button>
      {open ? <SizeChartDialog part={part} title={title} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
